import {t} from "@lingui/core/macro";
import * as React from "react";
import {useEffect, useRef} from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import Link from "@khanacademy/wonder-blocks-link";
import {semanticColor, spacing} from "@khanacademy/wonder-blocks-tokens";
import {HeadingMedium} from "@khanacademy/wonder-blocks-typography";
import {KhanmigoSettingsForm} from "@khan/khanmigo-settings";
import * as MobileApp from "@khan/mobile-app-integration";
import {RequestInfo} from "@khan/shared-core";
import {pureXsMax} from "@khan/shared-styles";

import externalLinkIcon from "@phosphor-icons/core/regular/arrow-square-out.svg";

interface Props {
    /* Adjust layout for full page view. */
    fullPageStyles?: boolean;
    /* Hide links when in LIHP (or possibly other contexts.) */
    hideLinks?: boolean;
    /* Callback to handle when the user clicks the "close" button. When
    omitted, the close button is not shown. */
    onClose?: () => void;
}

/**
 * A component to render a "quick form" for some editable Khanmigo preferences.
 */
export const Preferences = React.memo(
    ({fullPageStyles = false, hideLinks = false, onClose}: Props) => {
        // Focus the preferences panel when opened.
        const ref = useRef<HTMLAnchorElement>(null);
        useEffect(() => {
            ref.current?.focus();
        });

        // Make sure we tell the mobile app when the preferences panel is open
        // or closed, and allow the mobile app to close the panel if needed.
        // Note that if `onClose` is not provided, then it won't matter as we
        // won't be in the mobile app.
        MobileApp.Guide.useUXStack("preferences", {
            onClose: onClose ?? (() => {}),
            translatedTitle: t`Preferences`,
        });

        // Don't show the close button when in the mobile app.
        // It will show its own.
        const showPreferencesCloseButton =
            onClose != null && !RequestInfo.isMobileApp;

        return (
            <View style={styles.form}>
                <View
                    style={
                        fullPageStyles
                            ? styles.formHeader
                            : styles.compactHeader
                    }
                >
                    <HeadingMedium style={styles.headerText}>
                        {t`Preferences`}
                    </HeadingMedium>
                    {showPreferencesCloseButton && (
                        <Button
                            size="small"
                            kind="tertiary"
                            onClick={onClose}
                            ref={ref}
                        >
                            {t`Close`}
                        </Button>
                    )}
                    {fullPageStyles && <Strut size={spacing.large_24} />}
                </View>
                <View style={styles.scrollableWrapper}>
                    <View style={styles.lowerFormBody}>
                        <KhanmigoSettingsForm renderGuideLanguages={true} />
                        <Strut size={spacing.xLarge_32} />
                        {!hideLinks && (
                            <View
                                style={[
                                    styles.linkWrapper,
                                    styles.inputSection,
                                ]}
                            >
                                <Link
                                    href="/profile/me/khanmigo/settings#interests"
                                    target="_blank"
                                    style={styles.link}
                                    endIcon={
                                        <PhosphorIcon icon={externalLinkIcon} />
                                    }
                                >
                                    {t`Interests`}
                                </Link>
                            </View>
                        )}
                    </View>
                </View>
            </View>
        );
    },
);

const styles = {
    scrollableWrapper: {
        height: "100%",
        overflowY: "auto",
    },
    form: {
        justifyContent: "flex-start",
        backgroundColor: semanticColor.core.background.base.default,
        borderRadius: 8,
    },
    formHeader: {
        borderBottom: `1px solid ${semanticColor.core.border.neutral.subtle}`,
    },
    compactHeader: {
        paddingTop: spacing.xLarge_32,
        flexDirection: "row",
        justifyContent: "space-between",
        marginRight: spacing.large_24,
    },
    headerText: {
        paddingLeft: spacing.large_24,
        paddingRight: spacing.large_24,
    },
    lowerFormBody: {
        padding: "32px 24px",
        maxWidth: pureXsMax,
        flexShrink: 0,
    },
    inputSection: {
        flex: "0 0 auto",
    },
    link: {
        color: semanticColor.core.foreground.neutral.strong,
        fontSize: 16,
        display: "inline-flex",
        justifyContent: "space-between",
        paddingRight: spacing.small_12,
    },
    linkWrapper: {
        border: `1px solid ${semanticColor.core.border.neutral.subtle}`,
        borderRadius: 4,
        height: 40,
        padding: "8px 0px 8px 16px",
    },
} as const;
